import { Allow } from "./options";
export * from "./options";

class PartialJSON extends Error {}

class MalformedJSON extends Error {}

/**
 * Parse incomplete JSON
 * @param {string} jsonString Partial JSON to be parsed
 * @param {number} allowPartial Specify what types are allowed to be partial, see {@link Allow} for details
 * @returns The parsed JSON
 * @throws {PartialJSON} If the JSON is incomplete (related to the `allow` parameter)
 * @throws {MalformedJSON} If the JSON is malformed
 */
function parseJSON(jsonString: string, allowPartial: number = Allow.ALL): any {
    if (typeof jsonString !== "string") {
        throw new TypeError(`expecting str, got ${typeof jsonString}`);
    }
    if (!jsonString.trim()) {
        throw new Error(`${jsonString} is empty`);
    }
    return _parseJSON(jsonString.trim(), allowPartial);
}

const _parseJSON = (jsonString: string, allow: number) => {
    const length = jsonString.length;
    let index = 0;

    const markPartialJSON = (msg: string) => {
        throw new PartialJSON(`${msg} at position ${index}`);
    };

    const throwMalformedError = (msg: string) => {
        throw new MalformedJSON(`${msg} at position ${index}`);
    };

    const parseAny: () => any = () => {
        skipBlank();
        if (index >= length) markPartialJSON("Unexpected end of input");
        if (jsonString[index] === '"') return parseStr();
        if (jsonString[index] === "{") return parseObj();
        if (jsonString[index] === "[") return parseArr();
        if (jsonString.substring(index, index + 4) === "null" || (Allow.NULL & allow && length - index < 4 && "null".startsWith(jsonString.substring(index)))) {
            index += 4;
            return null;
        }
        if (jsonString.substring(index, index + 4) === "true" || (Allow.BOOL & allow && length - index < 4 && "true".startsWith(jsonString.substring(index)))) {
            index += 4;
            return true;
        }
        if (jsonString.substring(index, index + 5) === "false" || (Allow.BOOL & allow && length - index < 5 && "false".startsWith(jsonString.substring(index)))) {
            index += 5;
            return false;
        }
        if (jsonString.substring(index, index + 8) === "Infinity" || (Allow.INFINITY & allow && length - index < 8 && "Infinity".startsWith(jsonString.substring(index)))) {
            index += 8;
            return Infinity;
        }
        if (jsonString.substring(index, index + 9) === "-Infinity" || (Allow._INFINITY & allow && 1 < length - index && length - index < 9 && "-Infinity".startsWith(jsonString.substring(index)))) {
            index += 9;
            return -Infinity;
        }
        if (jsonString.substring(index, index + 3) === "NaN" || (Allow.NAN & allow && length - index < 3 && "NaN".startsWith(jsonString.substring(index)))) {
            index += 3;
            return NaN;
        }
        return parseNum();
    };

    const parseStr: () => string = () => {
        const start = index;
        let escape = false;
        let inStringValue = false; // Renamed from inTextProperty to be more general
        
        // Check if we're in any property value by looking back in the string
        let lookbackStr = "";
        for (let i = Math.max(0, index - 30); i < index; i++) {
            lookbackStr += jsonString[i];
        }
        // Look for any property pattern - any key followed by a colon
        if (lookbackStr.match(/["'][^"']+["']\s*:/)) {
            inStringValue = true;
            // console.log("Detected string property value at position:", index, "context:", lookbackStr);
        }

        index++; // skip initial quote
        
        const chars: string[] = []; // Store characters for building the string
        
        while (index < length) {
            const char = jsonString[index];
            
            if (char === '"' && !escape) {
                // Found a potential closing quote
                
                // Look ahead to next character after the quote
                let nextIndex = index + 1;
                while (nextIndex < length && /\s/.test(jsonString[nextIndex])) {
                    nextIndex++; // Skip whitespace
                }
                
                const nextChar = nextIndex < length ? jsonString[nextIndex] : "";
                
                // If we're in a string value and the next char is not a valid JSON delimiter
                // then this is likely an unescaped quote that needs handling
                if (inStringValue && 
                    nextChar && 
                    ![',', '}', ']', ':'].includes(nextChar)) {
                    
                    // console.log("Found unescaped quote at position:", index, 
                    //             "next char:", nextChar, 
                    //             "context:", jsonString.substring(Math.max(0, index - 15), Math.min(length, index + 15)));
                    
                    // Add the current quote as escaped
                    chars.push('\\');
                    chars.push('"');
                    index++;
                    continue;
                }
                
                // This is a real end of string
                break;
            }
            
            // Handle escape sequences
            if (char === '\\') {
                escape = !escape;
                chars.push(char);
            } else {
                if (escape) {
                    escape = false;
                }
                chars.push(char);
            }
            
            index++;
        }
        
        if (index < length && jsonString[index] === '"') {
            // We found a proper closing quote
            // Build the complete string with our manual processing
            const processedStr = '"' + chars.join('') + '"';
            index++; // Skip closing quote
            try {
                return JSON.parse(processedStr);
            } catch (e) {
                console.error("Failed to parse processed string:", e, processedStr);
                // Fall back to substring method if our manual processing fails
                try {
                    return JSON.parse(jsonString.substring(start, index));
                } catch (err) {
                    console.error("Failed to parse processed string fallback as well:", err, jsonString.substring(start, index));
                    throwMalformedError(String(err));
                }
            }
        } else if (Allow.STR & allow) {
            // Handling incomplete strings
            try {
                // Try to construct a valid JSON string from our partial string
                const processedStr = '"' + chars.join('') + '"';
                return JSON.parse(processedStr);
            } catch (e) {
                // console.error("Failed to parse incomplete string:", e);
                // String handling fallbacks for various cases
                try {
                    return JSON.parse(jsonString.substring(start, index) + '"');
                } catch (err) {
                    if (String(err).includes("Invalid escape")) {
                        try {
                            // Try removing the last incomplete escape sequence
                            const lastEscapeIndex = chars.lastIndexOf('\\');
                            if (lastEscapeIndex !== -1) {
                                const fixedChars = chars.slice(0, lastEscapeIndex);
                                return JSON.parse('"' + fixedChars.join('') + '"');
                            }
                        } catch {}
                    }
                    // Last resort, return the raw string content
                    return chars.join('');
                }
            }
        }
        
        markPartialJSON("Unterminated string literal");
    };

    const parseObj = () => {
        index++; // skip initial brace
        skipBlank();
        const obj: Record<string, any> = {};
        try {
            while (jsonString[index] !== "}") {
                skipBlank();
                if (index >= length && Allow.OBJ & allow) return obj;
                const key = parseStr();
                skipBlank();
                index++; // skip colon
                try {
                    const value = parseAny();
                    obj[key] = value;
                } catch (e) {
                    if (Allow.OBJ & allow) return obj;
                    else throw e;
                }
                skipBlank();
                if (jsonString[index] === ",") index++; // skip comma
            }
        } catch (e) {
            if (Allow.OBJ & allow) return obj;
            else markPartialJSON("Expected '}' at end of object");
        }
        index++; // skip final brace
        return obj;
    };

    const parseArr = () => {
        index++; // skip initial bracket
        const arr = [];
        try {
            while (jsonString[index] !== "]") {
                arr.push(parseAny());
                skipBlank();
                if (jsonString[index] === ",") {
                    index++; // skip comma
                }
            }
        } catch (e) {
            if (Allow.ARR & allow) {
                return arr;
            }
            markPartialJSON("Expected ']' at end of array");
        }
        index++; // skip final bracket
        return arr;
    };

    const parseNum = () => {
        if (index === 0) {
            if (jsonString === "-") throwMalformedError("Not sure what '-' is");
            try {
                return JSON.parse(jsonString);
            } catch (e) {
                if (Allow.NUM & allow)
                    try {
                        return JSON.parse(jsonString.substring(0, jsonString.lastIndexOf("e")));
                    } catch (e) {}
                throwMalformedError(String(e));
            }
        }

        const start = index;

        if (jsonString[index] === "-") index++;
        while (jsonString[index] && ",]}".indexOf(jsonString[index]) === -1) index++;

        if (index == length && !(Allow.NUM & allow)) markPartialJSON("Unterminated number literal");

        try {
            return JSON.parse(jsonString.substring(start, index));
        } catch (e) {
            if (jsonString.substring(start, index) === "-") markPartialJSON("Not sure what '-' is");
            try {
                return JSON.parse(jsonString.substring(start, jsonString.lastIndexOf("e")));
            } catch (e) {
                throwMalformedError(String(e));
            }
        }
    };

    const skipBlank = () => {
        while (index < length && " \n\r\t".includes(jsonString[index])) {
            index++;
        }
    };
    return parseAny();
};

const parse = parseJSON;

export { parse, parseJSON, PartialJSON, MalformedJSON, Allow };